import React, { CSSProperties, ReactElement, ReactNode, useState } from 'react';
import { ArrayHelpers } from 'formik';
import { useAlertDialog } from '../../../Utils/dialogContext';
import { PopoutEntite } from '../Utils/PopoutEntite';
import CreerLangue from './CreerLangue';
import ChampLangue from './DetailsLangue';
import { Langue } from '../../../../../common/src/entities/langue.entity';
import ChampMultiple from '../../Common/ChampMultiple';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { BiPencil, BiX } from 'react-icons/all';
import TooltipSimple from '../../Common/TooltipSimple';
import { cssTexte } from '../constantes';

const TITRE_BOUTON = 'Ajouter une langue';
const ENTRE_CHOISI_CONNU = 'Cette langue a déjà été rajouté! ';
const MAX_PARTICIPANT_TEXTE = 'Vous ne pouvez qu`ajouter un max de ';
const ELEMENT = 'élément(s)';

interface AjoutProps {
    helpers: ArrayHelpers;
    values: Langue[];
    avecUnBouton?: boolean;
    title?: string;
    maxSize?: number;
}

/**
 * Affiche la liste des langues disponible en BD et donne l'option de création d'une nouvelle langue
 *
 * @returns la liste des langues
 */
function AffichageAjoutLangue(props: AjoutProps): ReactElement {
    const bsAlert = useAlertDialog();

    /**
     * Ajoute une langue dans l'activité quand on le selectionne du dropdown. Affiche des alertes
     * si la langue fait déjà partie de l'activité.
     * @param langue la nouvelle langue
     */
    function gererOnchange(langue: Langue): void {
        if (langue) {
            if (props.values?.find((item) => item.id === langue.id)) {
                bsAlert(`${ENTRE_CHOISI_CONNU}`, 'warning');
            } else if (props.maxSize && props.values.length === props.maxSize) {
                bsAlert(`${MAX_PARTICIPANT_TEXTE} ${props.maxSize} ${ELEMENT}`, 'warning');
            } else {
                props.helpers.push(langue);
            }
        }
    }

    return (
        <PopoutEntite
            titre={TITRE_BOUTON}
            entite={Langue}
            isCreatable
            modalSize="lg"
            modalBody={(props) => <CreerLangue {...props} />}
            size="sm"
            disabled={props.maxSize === props.values.length ? true : false}
            onChange={(value) => gererOnchange(value as Langue)}
            avecUnBouton={props.avecUnBouton}
        />
    );
}

/**
 * Afficher le nom de la langue
 * @returns page html du formulaire
 */
function affichageLangue(langue: Langue) {
    return `${langue.name ? langue.name : ''}`;
}

/**
 * Permet de formater l'affichage d'une langue (bouton modifier + nom + bouton supprimer)
 *
 * @returns une ligne avec comme colonne chacun des éléments mis entre parenthèses
 * dans la description
 */
const AffichageElement = (props: {
    value: Langue;
    index: number;
    helpers: ArrayHelpers;
    affichageTexte: (value: Langue) => ReactNode;
}): ReactElement => {
    const { value, index, helpers, affichageTexte } = props;
    const { remove, replace } = helpers;
    const [showModal, setShowModal] = useState(false);
    const formProps = {
        id: value.id,
        valeursInitiales: value,
        modal: true,
        succes: (t: Langue) => {
            replace(index, t);
            setShowModal(false);
        },
    };
    return (
        <Row>
            <Col className="d-flex align-items-center">
                <Button variant="link" size="sm" onClick={() => setShowModal(true)}>
                    <BiPencil size="1.25em" />
                </Button>

                <span style={cssTexte}>{affichageTexte(value)}</span>
            </Col>
            <Col xs="auto">
                <TooltipSimple texte="Supprimer">
                    <Button
                        variant="link"
                        className="d-none d-sm-flex align-items-center text-muted px-0"
                        onClick={() => remove(index)}
                    >
                        <BiX size="1.25em" />
                    </Button>
                </TooltipSimple>
            </Col>
            <Modal
                scrollable
                show={showModal}
                onHide={() => {
                    setShowModal(false);
                }}
            >
                <ChampLangue {...formProps} />
            </Modal>
        </Row>
    );
};

/**
 * Fait appel a AffichageElement avec des paramètres donnés
 *
 */
const affichageElement = (
    value: Langue,
    index: number,
    helpers: ArrayHelpers,
    affichageTexte: (value: Langue) => ReactNode,
) => <AffichageElement {...{ value, index, helpers, affichageTexte }} />;

interface PropsChampMultipleLangue {
    id: string;
    avecUnBouton?: boolean;
    labelCol?: number;
    maxSize?: number;
}

/**
 * Permet de rajouter plusieurs langues dans une entité
 *
 * @returns Un tableau des langues sélectionné
 */
export default function ChampsMultipleLangue(props: PropsChampMultipleLangue): ReactElement {
    /**
     * Fait appel a la fonction AffichageAjoutLangue avec des paramètres donnés
     *
     */
    const affichageAjoutLangue = (values, helpers, maxSize?: number, title?: string, avecUnBouton?) => (
        <AffichageAjoutLangue
            helpers={helpers}
            values={values}
            maxSize={maxSize}
            title={title}
            avecUnBouton={avecUnBouton}
        />
    );

    return (
        <ChampMultiple
            entite={Langue}
            titre="Langue(s)"
            {...props}
            affichageAjout={affichageAjoutLangue}
            affichageTexte={(langue: Langue | number) => affichageLangue(langue as Langue)}
            affichageElement={(
                value: Langue | number,
                index: number,
                helpers: ArrayHelpers,
                affichageTexte: (value: Langue) => ReactNode,
            ) => affichageElement(value as Langue, index, helpers, affichageTexte)}
            labelCol={props.labelCol}
        />
    );
}
